// «Лента» — publicaţiile comunităţii.
//
// Structura e cea pe care o ştie oricine de pe o reţea socială: sus scrii, dedesubt
// curge lenta, fiecare publicaţie are reacţii şi comentarii. Diferenţa e la ce trimit
// legăturile: aici un `#BTC` deschide moneda în screener, nu o pagină de căutare
// generală. Într-o comunitate de trading, hashtag-ul e cel mai des un ticker.
//
// A doua diferenţă, cea care contează: o publicaţie poate purta o **idee** — monedă,
// intrare, stop, ţintă. Preţul îi dă verdictul singur, iar verdictul se vede pe card
// lângă text. Pe o reţea socială obişnuită o părere rămâne o părere pentru totdeauna;
// aici ea are un adevăr obiectiv care vine peste patruzeci de minute.
//
// Textul publicaţiilor **nu** se pune niciodată cu `innerHTML`. Fiecare bucată — text,
// hashtag, link — devine un nod separat, iar textul rămâne text. Altfel prima persoană
// care scrie un `<script>` în lentă ar rula cod în browserul tuturor.

import { el, clear, bus, fmtPrice } from '../core/util.js';
import { icon } from '../ui/icons.js';
import { session } from '../core/session.js';
import { toast, modal, contextMenu } from '../ui/components.js';
import { pictAvatar, comprima } from '../ui/avatars.js';
import { adu } from '../core/cache.js';

/**
 * Cerere scurta catre API.
 *
 * `metoda` trebuie data pe fata cand nu exista corp: fara ea iese un GET, iar o ruta
 * `POST` fara corp (salvare, fixare, blocare) raspunde tacut cu 404 si butonul pare
 * ca nu face nimic. Asa a stat ascunsa o vreme si aprecierea de pe publicatii.
 */
const json = async (path, body, metoda) => {
  const resp = await fetch(path, body || metoda
    ? { method: metoda || 'POST', headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined }
    : {});
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.detail || `Ошибка ${resp.status}`);
  return data;
};

/** „acum 5 минут" — o dată exactă într-o lentă nu ajută pe nimeni. */
function cand(ts) {
  const s = Math.floor(Date.now() / 1000) - ts;
  if (s < 60) return 'только что';
  if (s < 3600) return `${Math.floor(s / 60)} мин`;
  if (s < 86400) return `${Math.floor(s / 3600)} ч`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} д`;
  return new Date(ts * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/**
 * Textul publicaţiei, tăiat în bucăţi: cuvinte, hashtag-uri, adrese, @menţiuni, $monede.
 *
 * Se întoarce o listă de noduri, nu un şir de HTML — vezi nota din capul fişierului.
 */
function textBogat(text, onTag, onUser, onSimbol) {
  const bucati = [];
  // `\w` cu steagul `u` cuprinde si chirilica, deci un nick rusesc se coloreaza la
  // fel ca unul latin. Moneda ramane latina: asa sunt tickerele peste tot.
  const re = /(#[\w-]{1,40})|(@[\w-]{2,32})|(\$[A-Za-z][A-Za-z0-9]{1,14})|(https?:\/\/[^\s<]+)/gu;
  let ultim = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > ultim) bucati.push(document.createTextNode(text.slice(ultim, m.index)));
    const [brut, tag, user, simbol, url] = m;
    if (tag) {
      bucati.push(el('button', { class: 'fd-tag', text: brut, onclick: () => onTag(tag.slice(1)) }));
    } else if (user) {
      bucati.push(el('button', { class: 'fd-mention', text: brut, onclick: () => onUser(user.slice(1)) }));
    } else if (simbol) {
      bucati.push(el('button', { class: 'fd-cash', text: brut,
        onclick: () => onSimbol?.(simbol.slice(1).toUpperCase()) }));
    } else {
      // `rel="noopener noreferrer"` fiindcă legătura duce în afară şi e scrisă de altcineva:
      // fără el, pagina străină primeşte o referinţă la fereastra noastră.
      bucati.push(el('a', { class: 'fd-link', href: url, target: '_blank',
        rel: 'noopener noreferrer', text: url.length > 60 ? `${url.slice(0, 57)}…` : url }));
    }
    ultim = m.index + brut.length;
  }
  if (ultim < text.length) bucati.push(document.createTextNode(text.slice(ultim)));
  return bucati;
}

/**
 * Adresa de încorporare a unui video, dacă legătura e de la o sursă pe care o cunoaştem.
 *
 * Doar YouTube şi Vimeo: un `<iframe>` rulează cod străin în pagina noastră, deci lista
 * e albă şi scurtă, nu o regulă generală „orice pare video". Pentru YouTube folosim
 * `youtube-nocookie.com` — aceeaşi redare, fără să lăsăm urme în contul privitorului
 * doar pentru că a derulat lenta.
 */
function videoIncorporat(url) {
  try {
    const u = new URL(url);
    const gazda = u.hostname.replace(/^www\./, '');
    if (gazda === 'youtu.be') {
      const id = u.pathname.slice(1);
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (gazda === 'youtube.com' || gazda === 'm.youtube.com') {
      if (u.pathname === '/watch' && u.searchParams.get('v')) {
        return `https://www.youtube-nocookie.com/embed/${u.searchParams.get('v')}`;
      }
      const scurt = u.pathname.match(/^\/(?:shorts|embed)\/([\w-]+)/);
      if (scurt) return `https://www.youtube-nocookie.com/embed/${scurt[1]}`;
    }
    if (gazda === 'vimeo.com') {
      const id = u.pathname.match(/^\/(\d+)/);
      if (id) return `https://player.vimeo.com/video/${id[1]}`;
    }
  } catch { /* nu e o adresă validă — atunci nu e nici video */ }
  return null;
}

/** Primul video dintr-un text, dacă există. */
function primulVideo(text) {
  for (const m of text.matchAll(/https?:\/\/[^\s<]+/g)) {
    const e = videoIncorporat(m[0]);
    if (e) return e;
  }
  return null;
}

function camp(eticheta, valoare) {
  return el('div', { class: 'fd-idee-camp' }, [
    el('span', { class: 'fd-idee-et', text: eticheta }),
    el('span', { class: 'fd-idee-val', text: valoare }),
  ]);
}

/**
 * Cardul ideii dintr-o publicaţie: ce a scris omul înainte şi ce a făcut preţul după.
 *
 * Verdictul stă în dreapta, unde ajunge ochiul la sfârşit. Cât timp nu s-a întâmplat
 * nimic scrie „в работе" — o idee fără verdict nu e o idee proastă, e una tânără.
 */
function cardIdee(idea) {
  if (!idea) return null;
  const v = idea.track?.verdict || '';
  const lung = idea.direction === 'long';

  return el('div', { class: `fd-idee ${lung ? 'lung' : 'scurt'}` }, [
    el('div', { class: 'fd-idee-cap' }, [
      el('span', { class: 'fd-idee-sym', text: idea.symbol }),
      el('span', { class: `fd-idee-dir ${lung ? 'lung' : 'scurt'}`,
        text: lung ? 'LONG' : 'SHORT' }),
      idea.setup ? el('span', { class: 'fd-idee-setup', text: idea.setup }) : null,
      el('span', { class: `fd-verdict ${v || 'open'}`,
        text: v === 'worked' ? '✅ Цель взята' : v === 'failed' ? '❌ Стоп' : '⏳ В работе' }),
    ]),
    el('div', { class: 'fd-idee-cifre' }, [
      camp('Вход', fmtPrice(idea.entry)),
      camp('Стоп', idea.stops?.length ? fmtPrice(idea.stops[0]) : '—'),
      camp('Цель', idea.targets?.length ? fmtPrice(idea.targets[0]) : '—'),
      camp('Ход', `${(idea.track?.best_pct ?? 0) > 0 ? '+' : ''}${idea.track?.best_pct ?? 0}%`),
    ]),
  ]);
}

export class FeedSection {
  constructor(app) {
    this.app = app;
    const q = new URLSearchParams(location.search);
    this.filtruTag = q.get('tag') || '';
    this.filtruAutor = q.get('author') || '';
    this.filtruSimbol = q.get('symbol') || '';
    // Lenta are o singura ordine: data, cu prioritate pentru cei pe care ii urmaresti.
    // Filele de dinainte imparteau aceleasi publicatii in patru liste aproape identice,
    // iar omul trebuia sa aleaga inainte sa vada ceva. `scope` a ramas doar pentru
    // lista de salvate, care se deschide de pe profil.
    this.scope = 'all';
    this.postari = [];
  }

  async mount(host) {
    this.root = el('div', { class: 'fd-section' });
    host.append(this.root);
    this.#construieste();
    this.offSession = bus.on('session:change', () => { this.#construieste(); this.#incarca(); });
    await this.#incarca();
  }

  #construieste() {
    clear(this.root);
    this.listaEl = el('div', { class: 'fd-list' });
    this.filtruEl = el('div', { class: 'fd-filtru' });

    // Lenta doar arată. Se publică de pe profilul propriu — ca pe Facebook, unde scrii
    // „la tine" şi publicaţia apare în lenta tuturor. Aşa lenta rămâne un loc de citit,
    // iar publicaţia are un loc al ei de unde a pornit.
    this.root.append(el('div', { class: 'fd-doc' }, [
      session.authenticated
        ? el('button', { class: 'fd-catre-profil', onclick: () => this.#deschideUser(session.user.username) }, [
          (() => { const a = el('span', { class: 'fd-av' });
            pictAvatar(a, session.user, 40); return a; })(),
          el('span', { class: 'fd-catre-t', text: 'Что на рынке? Напишите у себя в профиле' }),
          icon('chevronRight', 12),
        ])
        : null,
      this.filtruEl,
      this.listaEl,
    ]));
    this.#syncFiltru();
  }

  /** Caseta de scris. Publică: o foloseşte profilul, care e acum singurul loc de
   *  unde se publică. O a doua casetă ar însemna două locuri de corectat. */
  compunere(dupaPublicare) {
    const zona = el('textarea', {
      class: 'fd-input', rows: 2, maxlength: 6000,
      placeholder: 'Что на рынке? #BTC отметит тему, $BTC — монету, @ник — участника',
      oninput: (e) => {
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 260)}px`;
        contorEl.textContent = `${e.target.value.length}/6000`;
      },
    });
    const contorEl = el('span', { class: 'fd-contor', text: '0/6000' });

    // Imaginea aleasă, ţinută până la publicare. Se micşorează în browser, ca avatarul:
    // prin reţea trece o poză de zeci de kiloocteţi, nu fotografia din telefon.
    let imagine = '';
    const previzualizare = el('div', { class: 'fd-preview' });
    previzualizare.style.display = 'none';
    const fisier = el('input', { type: 'file', accept: 'image/*', style: 'display:none' });

    const arataPreview = () => {
      clear(previzualizare).append(
        el('img', { class: 'fd-preview-img', src: imagine, alt: '' }),
        el('button', { class: 'fd-preview-x', title: 'Убрать', onclick: () => {
          imagine = ''; previzualizare.style.display = 'none'; clear(previzualizare);
          syncCaptura();
        } }, [icon('close', 11)]),
      );
      previzualizare.style.display = '';
    };

    fisier.onchange = async () => {
      const f = fisier.files?.[0];
      if (!f) return;
      try {
        imagine = await comprima(f);
        arataPreview();
      } catch (err) { toast(err.message, 'error'); }
      fisier.value = '';
    };

    // Captura luată de pe grafic, dacă aşteaptă una. Chipul apare doar când există ce
    // ataşa — un buton care de obicei nu face nimic e mai rău decât niciun buton.
    const chipCaptura = el('button', { class: 'fd-captura', type: 'button' });
    chipCaptura.style.display = 'none';
    const syncCaptura = async () => {
      const { iaCaptura } = await import('../ui/captura.js');
      const c = iaCaptura();
      if (!c || imagine) { chipCaptura.style.display = 'none'; return; }
      chipCaptura.style.display = '';
      clear(chipCaptura).append(
        el('img', { class: 'fd-captura-mic', src: c.url, alt: '' }),
        el('span', { text: c.eticheta ? `Прикрепить снимок ${c.eticheta}` : 'Прикрепить снимок графика' }),
        icon('plus', 11),
      );
    };
    chipCaptura.onclick = async () => {
      const { iaCaptura, goleste } = await import('../ui/captura.js');
      const c = iaCaptura();
      if (!c) return;
      imagine = c.url;
      arataPreview();
      goleste();
      chipCaptura.style.display = 'none';
    };
    syncCaptura();
    // Trei prilejuri de reverificare, fiindcă unul singur nu e de ajuns: evenimentul
    // (când captura se face cu caseta deja deschisă), atingerea casetei (când omul se
    // apucă de scris) şi întoarcerea în filă (când captura s-a făcut în altă parte).
    bus.on('captura:schimbata', () => syncCaptura());
    zona.addEventListener('focus', () => syncCaptura());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) syncCaptura();
    });

    // Ideea ataşată. Ascunsă până o ceri: cele mai multe publicaţii sunt doar text, iar
    // cinci câmpuri goale deasupra fiecărei casete descurajează scrisul.
    const idee = this.#campuriIdee();

    const oficialEl = el('label', { class: 'fd-oficial' }, [
      el('input', { type: 'checkbox' }),
      el('span', { text: 'От имени проекта' }),
    ]);
    // Vocea proiectului e a proprietarului. Ascunsă pentru restul, nu doar refuzată de
    // server: un buton care nu funcţionează e o promisiune încălcată.
    if (session.user?.role !== 'owner') oficialEl.style.display = 'none';

    const trimite = async () => {
      const text = zona.value.trim();
      const cuIdee = idee.valoare();
      // O poză e o publicaţie în sine: nu cerem şi text dacă există imagine sau idee.
      if (!text && !imagine && !cuIdee) return;
      try {
        await json('/api/feed', { text, shot: imagine, idea: cuIdee,
          official: oficialEl.querySelector('input').checked });
        zona.value = '';
        zona.style.height = '';
        imagine = '';
        previzualizare.style.display = 'none';
        clear(previzualizare);
        contorEl.textContent = '0/6000';
        idee.reseteaza();
        syncCaptura();
        await dupaPublicare?.();
        toast(cuIdee ? 'Идея опубликована — цену отследим сами' : 'Опубликовано', 'success');
      } catch (err) {
        toast(err.message, 'error');
      }
    };

    const av = el('span', { class: 'fd-av mare' });
    pictAvatar(av, session.user, 44);

    return el('div', { class: 'fd-compune' }, [
      av,
      el('div', { class: 'fd-compune-corp' }, [
        zona,
        chipCaptura,
        previzualizare,
        idee.panou,
        el('div', { class: 'fd-compune-jos' }, [
          el('button', { class: 'fd-atas', title: 'Добавить картинку',
            onclick: () => fisier.click() }, [icon('image', 14)]),
          el('button', { class: 'fd-atas', title: 'Прикрепить идею со входом и стопом',
            onclick: () => idee.comuta() }, [icon('lightbulb', 14)]),
          // Captura de pe grafic. Butonul e mereu aici, nu doar cand exista ceva de
          // atasat: un buton care apare si dispare nu se invata niciodata, iar cand
          // buzunarul e gol are ce raspunde — spune de unde se ia o captura.
          el('button', {
            class: 'fd-atas', title: 'Прикрепить снимок графика',
            onclick: async () => {
              const { iaCaptura, goleste } = await import('../ui/captura.js');
              const c = iaCaptura();
              if (!c) {
                toast('Откройте график, нарисуйте и нажмите «Снимок графика»', 'info');
                return;
              }
              imagine = c.url;
              arataPreview();
              goleste();
              chipCaptura.style.display = 'none';
              toast('Снимок прикреплён', 'success');
            },
          }, [icon('camera', 14)]),
          fisier, oficialEl, contorEl,
          el('button', { class: 'btn primary', text: 'Опубликовать', onclick: trimite }),
        ]),
      ]),
    ]);
  }

  /**
   * Câmpurile ideii: monedă, direcţie, intrare, stop, ţintă.
   *
   * Atât şi nimic mai mult. Fiecare câmp în plus e un motiv să nu scrii ideea, iar o
   * idee nescrisă nu poate fi verificată de nimeni.
   */
  #campuriIdee() {
    const panou = el('div', { class: 'fd-idee-form' });
    panou.style.display = 'none';

    const simbol = el('input', { class: 'fd-if', type: 'text', placeholder: 'Монета, напр. SOLUSDT' });
    const directie = el('select', { class: 'fd-if' }, [
      el('option', { value: 'long', text: 'LONG' }),
      el('option', { value: 'short', text: 'SHORT' }),
    ]);
    const intrare = el('input', { class: 'fd-if', type: 'number', step: 'any', placeholder: 'Вход' });
    const stop = el('input', { class: 'fd-if', type: 'number', step: 'any', placeholder: 'Стоп' });
    const tinta = el('input', { class: 'fd-if', type: 'number', step: 'any', placeholder: 'Цель' });

    panou.append(
      el('div', { class: 'fd-idee-form-rand' }, [simbol, directie]),
      el('div', { class: 'fd-idee-form-rand' }, [intrare, stop, tinta]),
      el('p', { class: 'fd-idee-form-nota',
        text: 'Дальше цену смотрит платформа: цель раньше стопа — «сработала», стоп — «нет». '
          + 'Изменить вердикт нельзя ни вам, ни нам.' }),
    );

    return {
      panou,
      comuta: () => {
        panou.style.display = panou.style.display === 'none' ? '' : 'none';
        if (panou.style.display === '') simbol.focus();
      },
      reseteaza: () => {
        panou.style.display = 'none';
        for (const c of [simbol, intrare, stop, tinta]) c.value = '';
      },
      valoare: () => {
        if (panou.style.display === 'none') return null;
        const s = simbol.value.trim().toUpperCase();
        const e = parseFloat(intrare.value);
        if (!s || !(e > 0)) return null;
        return {
          symbol: s,
          direction: directie.value,
          entry: e,
          stops: parseFloat(stop.value) > 0 ? [parseFloat(stop.value)] : [],
          targets: parseFloat(tinta.value) > 0 ? [parseFloat(tinta.value)] : [],
          exchange: this.app?.exchange || 'binance',
          market: this.app?.market || 'futures',
        };
      },
    };
  }

  #syncFiltru() {
    clear(this.filtruEl);
    const activ = this.filtruTag || this.filtruAutor || this.filtruSimbol;
    if (!activ) { this.filtruEl.style.display = 'none'; return; }
    this.filtruEl.style.display = '';
    this.sentimentEl = this.filtruSimbol ? el('span', { class: 'fd-sent' }) : null;
    this.filtruEl.append(
      el('span', { class: 'fd-filtru-t',
        text: this.filtruTag ? `#${this.filtruTag}`
          : this.filtruSimbol ? `$${this.filtruSimbol}` : `@${this.filtruAutor}` }),
      this.sentimentEl,
      el('button', { class: 'fd-filtru-x', onclick: () => {
        this.filtruTag = ''; this.filtruAutor = ''; this.filtruSimbol = '';
        history.replaceState({}, '', '/feed/');
        this.#syncFiltru(); this.#incarca();
      } }, [icon('close', 10), el('span', { text: 'Сбросить' })]),
    );
    if (this.filtruSimbol) this.#incarcaSentiment();
  }

  /** Ce cred oamenii despre moneda din filtru. Informaţie despre oameni, nu despre preţ. */
  async #incarcaSentiment() {
    const simbol = this.filtruSimbol;
    try {
      const d = await json(`/api/social/sentiment?symbol=${encodeURIComponent(simbol)}`);
      if (this.filtruSimbol !== simbol || !this.sentimentEl) return;
      clear(this.sentimentEl);
      if (d.bull_pct === null || d.bull_pct === undefined) {
        this.sentimentEl.append(el('span', { class: 'fd-sent-x', text: 'мнений пока мало' }));
        return;
      }
      this.sentimentEl.append(
        el('span', { class: 'fd-sent-bara' }, [
          el('span', { class: 'fd-sent-sus', style: `width:${d.bull_pct}%` }),
        ]),
        el('span', { class: 'fd-sent-t', text: `${d.bull_pct}% 🚀 · ${100 - d.bull_pct}% 🐻` }),
      );
    } catch { /* sentimentul e un plus, nu un motiv de eroare pe ecran */ }
  }

  async #incarca() {
    // Cardul poate fi imprumutat de profil, unde nu exista lista lentei. Atunci
    // reincarcarea o face gazda — altfel am incerca sa desenam intr-un element inexistent.
    if (this.laReincarcare) { await this.laReincarcare(); return; }
    const q = new URLSearchParams();
    if (this.filtruTag) q.set('tag', this.filtruTag);
    if (this.filtruAutor) q.set('author', this.filtruAutor);
    if (this.filtruSimbol) q.set('symbol', this.filtruSimbol);
    if (this.scope !== 'all') q.set('scope', this.scope);   // «Сохранённое», de pe profil
    const adresa = `/api/feed${q.toString() ? `?${q}` : ''}`;

    // Desenam pe loc ce stim din vizita de dinainte, apoi intrebam serverul si
    // redesenam doar daca s-a schimbat ceva. Fara asta, fiecare intoarcere la lenta
    // incepea cu o pagina goala.
    try {
      await adu(adresa, () => json(adresa), (d) => {
        this.postari = d.items || [];
        this.#randeaza();
      });
    } catch {
      if (!this.postari.length) { this.postari = []; this.#randeaza(); }
    }
  }

  /** Rutare interna cand se poate, reincarcare doar ca ultima varianta. */
  #duLa(drum) {
    if (this.app?.navigate) this.app.navigate(drum);
    else location.href = drum;
  }

  #deschideTag(tag) {
    if (!this.listaEl) { this.#duLa(`/feed/?tag=${encodeURIComponent(tag)}`); return; }
    this.filtruTag = tag; this.filtruAutor = ''; this.filtruSimbol = '';
    history.replaceState({}, '', `/feed/?tag=${encodeURIComponent(tag)}`);
    this.#syncFiltru(); this.#incarca();
  }

  #deschideSimbol(simbol) {
    if (!this.listaEl) { this.#duLa(`/feed/?symbol=${encodeURIComponent(simbol)}`); return; }
    this.filtruSimbol = simbol; this.filtruTag = ''; this.filtruAutor = '';
    history.replaceState({}, '', `/feed/?symbol=${encodeURIComponent(simbol)}`);
    this.#syncFiltru(); this.#incarca();
  }

  #deschideUser(nume) {
    // Rutare interna, nu `location.href`: o reincarcare completa ar reporni aplicatia
    // si ar aduce din nou tot ce e deja in memorie.
    const drum = `/profile/${encodeURIComponent(nume)}/`;
    if (this.app?.navigate) this.app.navigate(drum);
    else location.href = drum;
  }

  #randeaza() {
    clear(this.listaEl);
    if (!this.postari.length) {
      this.listaEl.append(el('div', { class: 'fd-gol' }, [
        el('div', { text: this.scope === 'saved' ? 'Вы ещё ничего не сохранили'
          : this.filtruTag || this.filtruAutor || this.filtruSimbol
            ? 'По этому фильтру пока пусто' : 'Пока никто ничего не написал' }),
        el('div', { class: 'ideas-empty-sub',
          text: 'Расскажите, что видите на рынке — публикация уйдёт в общую ленту.' }),
      ]));
      return;
    }
    for (const p of this.postari) this.listaEl.append(this.card(p));
  }

  /**
   * Cardul unei publicatii.
   *
   * Public, fiindca il foloseste si profilul. Un al doilea desen ar insemna ca reactiile
   * si verdictul apar intr-un loc si lipsesc in celalalt — exact ce s-a intamplat pana
   * acum, cand profilul avea inca vechea inimioara.
   */
  card(p) {
    const av = el('span', { class: 'fd-av' });
    pictAvatar(av, p.author, 40);

    const comentariiEl = el('div', { class: 'fd-comentarii' });
    comentariiEl.style.display = 'none';

    const meniuEl = el('button', { class: 'fd-meniu', title: 'Ещё' }, [icon('ellipsis', 12)]);
    meniuEl.onclick = () => this.#meniuPost(p, meniuEl);

    return el('article', { class: `fd-card${p.official ? ' oficial' : ''}` }, [
      p.pinned ? el('div', { class: 'fd-fixat' }, [
        icon('bookmarkOn', 10), el('span', { text: 'Закреплено' }),
      ]) : null,

      el('div', { class: 'fd-cap' }, [
        el('button', { class: 'fd-av-btn', onclick: () => this.#deschideUser(p.author.username) }, [av]),
        el('div', { class: 'fd-cine' }, [
          el('div', { class: 'fd-nume-rand' }, [
            el('button', { class: 'fd-nume', text: p.author.username,
              onclick: () => this.#deschideUser(p.author.username) }),
            p.official ? el('span', { class: 'fd-marca', text: 'ПРОЕКТ' }) : null,
            el('span', { class: 'fd-rang', text: p.author.status?.name || '' }),
          ]),
          el('span', { class: 'fd-cand', text: cand(p.created_at) }),
        ]),
        session.authenticated ? meniuEl : null,
      ]),

      p.text ? this.#textPublicatie(p.text) : null,

      cardIdee(p.idea),

      p.shot ? this.#poza(p.shot) : null,

      // Videoul se încarcă abia când publicaţia intră în ecran: douăzeci de `iframe`
      // deodată ar aduce douăzeci de playere într-o pagină din care se vede unul.
      this.#video(p),

      el('div', { class: 'fd-actiuni' }, [
        this.#inima(p),
        (() => {
          // Butonul isi tine singur numarul la zi: caseta de scris il cheama dupa
          // fiecare comentariu nou, altfel cifra ramane cea de la incarcarea paginii.
          const b = el('button', { class: 'fd-act',
            onclick: () => this.#comutaComentarii(p, comentariiEl) });
          // Cifra se arata mereu, si cand e zero: „cate comentarii are" e o intrebare
          // cu raspuns, iar un buton gol nu-l da.
          comentariiEl.sincronizeaza = () => clear(b).append(
            icon('comments', 12), el('span', { text: String(p.comments || 0) }));
          comentariiEl.sincronizeaza();
          return b;
        })(),
        session.authenticated ? this.#salvare(p) : null,
        session.authenticated && !p.mine
          ? el('button', { class: 'fd-act', title: 'Написать лично',
            onclick: () => this.scrieLui(p.author.username) }, [icon('send', 11)])
          : null,
      ]),
      comentariiEl,
    ]);
  }

  /**
   * Textul publicaţiei, pe paragrafe, scurtat dacă e lung.
   *
   * Un «разбор» de patru mii de semne desfăşurat întreg împinge următoarele publicaţii
   * atât de jos încât lenta pare goală. De aceea se taie la o înălţime şi se deschide
   * la cerere — dar se taie **vizual**, cu textul deja în pagină: cine caută cu Ctrl+F
   * sau citeşte cu un cititor de ecran îl găseşte oricum.
   */
  #textPublicatie(text) {
    const gazda = el('div', { class: 'fd-text' });
    // Rândurile goale despart paragrafe. Fără asta, un разбор ajunge un bloc compact
    // pe care nimeni nu-l citeşte, oricât de bun ar fi.
    for (const parte of text.split(/\n{2,}/)) {
      if (!parte.trim()) continue;
      gazda.append(el('p', { class: 'fd-para' }, textBogat(
        parte, (t) => this.#deschideTag(t), (u) => this.#deschideUser(u),
        (sim) => this.#deschideSimbol(sim))));
    }

    if (text.length > 900) {
      gazda.classList.add('taiat');
      const buton = el('button', { class: 'fd-mai-mult', text: 'Читать дальше' });
      buton.onclick = () => { gazda.classList.remove('taiat'); buton.remove(); };
      const cutie = el('div', { class: 'fd-text-cutie' }, [gazda, buton]);
      return cutie;
    }
    return gazda;
  }

  /** Inima. Un singur gest — a doua apăsare îl retrage. */
  /**
   * Imaginea publicaţiei.
   *
   * Locul ei e rezervat **înainte** ca poza să ajungă: lăţimea şi înălţimea vin în
   * coada adresei, puse la încărcare, iar din ele iese un `aspect-ratio`. Fără asta,
   * textul de sub imagine sare în jos exact când începeai să-l citeşti.
   *
   * `loading="lazy"` şi `decoding="async"`: textul întregii lente se aşază primul, iar
   * pozele vin după, pe măsură ce ajungi la ele. Asta e ordinea în care se citeşte
   * oricum o lentă.
   */
  #poza(adresa) {
    const u = new URLSearchParams(adresa.split('?')[1] || '');
    const lat = Number(u.get('w')) || 0;
    const inalt = Number(u.get('h')) || 0;

    const cutie = el('div', { class: 'fd-media' });
    // Fără dimensiuni (publicaţiile de dinainte) lăsăm un dreptunghi obişnuit: mai bine
    // o rezervare aproximativă decât niciuna.
    cutie.style.aspectRatio = lat && inalt ? `${lat} / ${inalt}` : '16 / 10';

    const img = el('img', {
      class: 'fd-media-img', src: adresa, alt: '', loading: 'lazy', decoding: 'async',
    });
    img.onload = () => { cutie.classList.add('gata'); cutie.style.aspectRatio = ''; };
    img.onerror = () => { cutie.classList.add('gata'); cutie.style.aspectRatio = ''; };

    cutie.append(img);
    cutie.onclick = async () => {
      const { deschidePoza } = await import('../ui/lightbox.js');
      deschidePoza(adresa);
    };
    return cutie;
  }

  /** Inima. Un singur gest — a doua apăsare îl retrage. */
  #inima(p) {
    const b = el('button', { class: 'fd-act', title: 'Нравится' });
    const deseneaza = () => {
      clear(b).append(icon(p.liked ? 'heartFull' : 'heart', 13),
        el('span', { text: p.likes ? String(p.likes) : '' }));
      b.classList.toggle('on', !!p.liked);
    };
    b.onclick = async () => {
      if (!session.authenticated) { toast('Войдите, чтобы отмечать', 'info'); return; }
      try {
        const d = await json(`/api/feed/${p.id}/like`, null, 'POST');
        Object.assign(p, d.post);
        deseneaza();
      } catch (err) { toast(err.message, 'error'); }
    };
    deseneaza();
    return b;
  }

  #salvare(p) {
    const b = el('button', { class: 'fd-act', title: 'Сохранить' });
    const deseneaza = () => {
      clear(b).append(icon(p.saved ? 'bookmarkOn' : 'bookmark', 12));
      b.classList.toggle('on', !!p.saved);
    };
    b.onclick = async () => {
      try {
        const d = await json(`/api/feed/${p.id}/save`, null, 'POST');
        Object.assign(p, d.post);
        deseneaza();
      } catch (err) { toast(err.message, 'error'); }
    };
    deseneaza();
    return b;
  }

  /** Meniul „…": fixare, plângere, blocare, ştergere. */
  #meniuPost(p, ancora) {
    const staff = ['owner', 'admin'].includes(session.user?.role);
    contextMenu(ancora, [
      p.mine ? {
        label: p.pinned ? 'Открепить от профиля' : 'Закрепить в профиле', icon: 'bookmarkOn',
        onClick: async () => {
          try {
            await json(`/api/feed/${p.id}/pin`, null, 'POST');
            await this.#incarca();
          } catch (err) { toast(err.message, 'error'); }
        },
      } : null,
      !p.mine ? { label: 'Пожаловаться', icon: 'warning', onClick: () => this.#plangere(p) } : null,
      !p.mine ? {
        label: `Заблокировать ${p.author.username}`, icon: 'eyeOff',
        onClick: async () => {
          try {
            const d = await json(`/api/social/block/${encodeURIComponent(p.author.username)}`, null, 'POST');
            toast(d.blocked ? 'Публикации этого участника скрыты' : 'Блокировка снята', 'success');
            await this.#incarca();
          } catch (err) { toast(err.message, 'error'); }
        },
      } : null,
      p.mine || staff ? { label: 'Удалить', icon: 'trash', onClick: () => this.#sterge(p) } : null,
    ].filter(Boolean));
  }

  #plangere(p) {
    const motiv = el('textarea', { class: 'fd-input', rows: 3, maxlength: 300,
      placeholder: 'Что не так с этой публикацией?' });
    modal({
      title: 'Пожаловаться',
      width: 420,
      body: el('div', { class: 'fd-plang' }, [
        el('p', { class: 'set-hint',
          text: 'Жалобу увидят модераторы. Автор не узнает, кто её отправил.' }),
        motiv,
      ]),
      actions: [
        { label: 'Отмена', onClick: (c) => c() },
        { label: 'Отправить', primary: true, onClick: async (c) => {
          try {
            await json(`/api/feed/${p.id}/report`, { reason: motiv.value });
            toast('Жалоба отправлена', 'success');
          } catch (err) { toast(err.message, 'error'); }
          c();
        } },
      ],
    });
  }

  /** Cadrul video al publicaţiei, creat leneş când ajunge pe ecran. */
  #video(p) {
    const sursa = primulVideo(p.text || '');
    if (!sursa) return null;
    const cutie = el('div', { class: 'fd-video' });
    const obs = new IntersectionObserver((intrari) => {
      if (!intrari.some((i) => i.isIntersecting)) return;
      obs.disconnect();
      cutie.append(el('iframe', {
        src: sursa, loading: 'lazy', allowfullscreen: '',
        // Doar domeniul, nu adresa completă: sursa nu află pe ce pagină anume e omul,
        // dar află de pe ce site — iar YouTube refuză încorporarea fără asta şi arată
        // „Ошибка 153" în loc de film. Măsurat, nu presupus.
        referrerpolicy: 'strict-origin-when-cross-origin',
        allow: 'accelerometer; clipboard-write; encrypted-media; picture-in-picture',
      }));
    }, { rootMargin: '200px' });
    obs.observe(cutie);
    return cutie;
  }

  async #sterge(p) {
    if (!confirm('Удалить публикацию?')) return;
    try {
      await json(`/api/feed/${p.id}`, null, 'DELETE');
      await this.#incarca();
    } catch (err) { toast(err.message, 'error'); }
  }

  async #comutaComentarii(p, gazda) {
    if (gazda.style.display !== 'none') { gazda.style.display = 'none'; return; }
    gazda.style.display = '';
    clear(gazda).append(el('div', { class: 'fd-gol-mic', text: 'Загружаем…' }));
    try {
      const d = await json(`/api/feed/${p.id}/comments`);
      this.#randComentarii(p, gazda, d.items || []);
    } catch (err) {
      clear(gazda).append(el('div', { class: 'fd-gol-mic', text: err.message }));
    }
  }

  #randComentarii(p, gazda, lista) {
    clear(gazda);
    for (const c of lista) gazda.append(this.#comentariu(p, gazda, c));
    if (!session.authenticated) return;
    gazda.append(this.#casetaComentariu(p, gazda, 0, 'Написать комментарий… @ник ответит адресно'));
  }

  /**
   * Un comentariu, cu răspunsurile lui dedesubt.
   *
   * O singură treaptă de adâncime: un răspuns la un răspuns se prinde tot de firul de
   * sus. Firele adânci arată bine în captura de ecran şi sunt de necitit pe telefon,
   * unde fiecare treaptă mănâncă un centimetru din lăţime.
   */
  #comentariu(p, gazda, c) {
    const av = el('span', { class: 'fd-av mic' });
    pictAvatar(av, c.author, 28);

    const inima = el('button', { class: 'fd-com-act' });
    const deseneazaInima = () => {
      clear(inima).append(icon(c.liked ? 'heartFull' : 'heart', 11),
        el('span', { text: c.likes ? String(c.likes) : '' }));
      inima.classList.toggle('on', !!c.liked);
    };
    inima.onclick = async () => {
      try {
        const d = await json(`/api/feed/comments/${c.id}/like`, null, 'POST');
        c.likes = d.likes; c.liked = d.liked;
        deseneazaInima();
      } catch (err) { toast(err.message, 'error'); }
    };
    deseneazaInima();

    const raspunsuri = el('div', { class: 'fd-com-raspunsuri' });
    for (const r of c.replies || []) raspunsuri.append(this.#comentariu(p, gazda, r));

    const zonaRaspuns = el('div', { class: 'fd-com-raspunde' });
    zonaRaspuns.style.display = 'none';

    return el('div', { class: 'fd-com' }, [
      av,
      el('div', { class: 'fd-com-corp-tot' }, [
        el('div', { class: 'fd-com-corp' }, [
          el('div', { class: 'fd-com-cap' }, [
            el('button', { class: 'fd-nume mic', text: c.author.username,
              onclick: () => this.#deschideUser(c.author.username) }),
            el('span', { class: 'fd-cand', text: cand(c.created_at) }),
          ]),
          el('div', { class: 'fd-com-text' }, textBogat(
            c.text, (t) => this.#deschideTag(t), (u) => this.#deschideUser(u),
            (sim) => this.#deschideSimbol(sim))),
        ]),
        el('div', { class: 'fd-com-jos' }, [
          inima,
          session.authenticated ? el('button', {
            class: 'fd-com-act', text: 'Ответить',
            onclick: () => {
              const deschis = zonaRaspuns.style.display !== 'none';
              zonaRaspuns.style.display = deschis ? 'none' : '';
              if (!deschis && !zonaRaspuns.children.length) {
                zonaRaspuns.append(this.#casetaComentariu(
                  p, gazda, c.id, `Ответить ${c.author.username}…`, `@${c.author.username} `));
              }
              zonaRaspuns.querySelector('input')?.focus();
            },
          }) : null,
          c.mine || ['owner', 'admin'].includes(session.user?.role) ? el('button', {
            class: 'fd-com-act', text: 'Удалить',
            onclick: async () => {
              try {
                await json(`/api/feed/comments/${c.id}`, null, 'DELETE');
                const d = await json(`/api/feed/${p.id}/comments`);
                p.comments = (d.items || []).reduce((n, x) => n + 1 + (x.replies?.length || 0), 0);
                gazda.sincronizeaza?.();
                this.#randComentarii(p, gazda, d.items || []);
              } catch (err) { toast(err.message, 'error'); }
            },
          }) : null,
        ]),
        zonaRaspuns,
        raspunsuri,
      ]),
    ]);
  }

  /** Caseta de scris un comentariu sau un răspuns. */
  #casetaComentariu(p, gazda, parintele, placeholder, prefix = '') {
    const camp = el('input', { class: 'fd-com-input', type: 'text', maxlength: 800,
      placeholder, value: prefix });
    const trimite = async () => {
      const text = camp.value.trim();
      if (!text) return;
      camp.value = '';
      try {
        const d = await json(`/api/feed/${p.id}/comments`,
          { text, parent_id: parintele || 0 });
        // Numărul de pe buton numără tot firul, răspunsurile incluse.
        p.comments = (d.items || []).reduce((n, c) => n + 1 + (c.replies?.length || 0), 0);
        gazda.sincronizeaza?.();
        this.#randComentarii(p, gazda, d.items || []);
      } catch (err) { toast(err.message, 'error'); }
    };
    camp.onkeydown = (e) => { if (e.key === 'Enter') trimite(); };
    return el('div', { class: 'fd-com-scrie' }, [
      camp,
      el('button', { class: 'fd-com-send', onclick: trimite }, [icon('send', 12)]),
    ]);
  }

  /**
   * Deschide conversatia cu cineva, jos-dreapta.
   *
   * Publica, fiindca o cheama si profilul: aceeasi conversatie trebuie sa arate la fel
   * de oriunde ai deschide-o. Fereastra insasi traieste in `ui/messages.js` — un mesaj
   * se scrie in timp ce te uiti la altceva, iar o fereastra modala iti ia tocmai acel
   * altceva.
   */
  async scrieLui(nume) {
    const { deschideMesaje } = await import('../ui/messages.js');
    deschideMesaje(nume);
  }

  unmount() {
    this.offSession?.();
    this.root?.remove();
  }
}
